import React, { useEffect, useState } from 'react';
import { useCurrentuser } from '@uplift/contexts';
import { Box, Container } from '@mui/material';
import { AddressVerificationProvider, EditableSectionListContext, EditableSectionTipWidth, EditableSectionsContext, SuggestDesktopModal, useScrollableView } from '@uplift/ui-v2';
import { AboutYouSection, BioSection, ClinicalExperienceSection, CredentialingSection, PracticeSection } from './sections';
import { useQuery } from '@tanstack/react-query';
import { ProviderMatchingProfileBookmark, getProviderAvatarAsset, getProviderDetails, getProviderProfile } from '@uplift/api';
import { ProfileContext } from './profile.context';
import { Loading } from '@uplift/pages';
import { PROFILE_FUE_STATUS_QUERY_KEY, PROFILE_QUERY_KEY, ProfileOnboardingStatus, usePreprocessProfileEditKeyChange, useProfileOnboardingStatus } from './profile.utils';
import { ProfileFue } from './FUE/ProfileFue.component';
import { ProfileAlertBar } from './components/ProfileAlertBar.component';
import { ProfileLayout } from './components/ProfileLayout.component';
import { ProfilePageHeader, ProfileTabEnum } from './components/ProfilePageHeader.component';
import { ProfileInReviewPreview } from './components/ProfileInReviewPreview.component';

export const ProfilePage: React.FC = () => {
  const me = useCurrentuser();
  const [ isEditProfile, setIsEditProfile ] = useState(false);
  const { data: profile, isInitialLoading: profileLoading } = useQuery({
    queryKey: PROFILE_QUERY_KEY(me.id),
    queryFn: async () => {
      const { data: profileData } = await getProviderProfile(me.id);
      const { data: avatarAssetData } = await getProviderAvatarAsset(me.id);

      return { ...profileData, ...avatarAssetData };
    }
  });
  const { data: fueStatus = null, isInitialLoading: fueStatusLoading } = useQuery(PROFILE_FUE_STATUS_QUERY_KEY(me.id), async () => {
    const result = await getProviderDetails(me.id);

    return result.data.matchingProfileBookmark;
  }, { retry: false });
  const { profileOnboardingStatus, isLoading: onboardingStatusLoading } = useProfileOnboardingStatus(profile);

  if (profileLoading || fueStatusLoading || onboardingStatusLoading) {
    return <Loading />;
  }

  if (fueStatus && fueStatus !== ProviderMatchingProfileBookmark.complete) {
    return (
      <AddressVerificationProvider shouldReverifyAddress={false} parsedVerifiedAddress={null}>
        <ProfileContext.Provider value={{ profile, loading: profileLoading }}>
          <ProfileFue status={fueStatus} />
          <SuggestDesktopModal />
        </ProfileContext.Provider>
      </AddressVerificationProvider>
    );
  }

  const isInReview = profileOnboardingStatus === ProfileOnboardingStatus.in_review;
  const showPreviewAsPage = isInReview && !isEditProfile;

  return (
    <AddressVerificationProvider shouldReverifyAddress={false} parsedVerifiedAddress={null}>
      <ProfileLayout isOnboarding={profileOnboardingStatus !== null}>
        <ProfileContext.Provider value={{ profile, loading: profileLoading }}>

          <ProfileAlertBar
            status={profileOnboardingStatus}
            onToggleIsEditProfile={() => setIsEditProfile(!isEditProfile)}
            isEditProfile={isEditProfile}
          />

          {showPreviewAsPage && <ProfileInReviewPreview />}

          {!showPreviewAsPage && (
            <>
              <ProfilePageHeader preventPreview={isInReview} />
              <ProfileSections />
            </>
          )}

        </ProfileContext.Provider>
      </ProfileLayout>
    </AddressVerificationProvider>
  );
};


const ProfileSections: React.VFC = () => {
  const { scrollTo } = useScrollableView();
  const preprocessEditKeyChange = usePreprocessProfileEditKeyChange();
  const [ editKey, setEditKey ] = useState<string | null>(null);
  const [ editIndex, setEditIndex ] = useState(0);
  const [ tipWidth, setTipWidth ] = useState<number | undefined>();

  useEffect(() => {
    let timeout: NodeJS.Timeout = undefined;

    if (editKey) {

      timeout = setTimeout(() => {
        const element = document.getElementById(editKey);

        if (element) {
          scrollTo({ top: element.offsetTop - 200, behavior: 'smooth' });
        }
      });

    }

    return () => {
      clearTimeout(timeout);
    };
  }, [ editKey, scrollTo ]);

  return (
    <EditableSectionsContext.Provider value={{ editKey, setEditKey, preprocessEditKeyChange, tipWidth }}>
      <EditableSectionListContext.Provider value={{ editIndex, setEditIndex }}>
        <Container>
          <Box pt={{ xs: 3, sm: 5 }} pb={{ xs: 32 }} display="flex" mx={{ xs: -2, sm: 0 }}>

            <Box display="flex" flexDirection="column" gap="2.5rem" width="792px" maxWidth="792px" flexGrow={1}>
              <div id={ProfileTabEnum.aboutYou}>
                <AboutYouSection />
              </div>

              <div id={ProfileTabEnum.bio}>
                <BioSection />
              </div>

              <div id={ProfileTabEnum.yourPractice}>
                <PracticeSection />
              </div>

              <div id={ProfileTabEnum.clinicalExperience}>
                <ClinicalExperienceSection />
              </div>

              <div id={ProfileTabEnum.crendentialing}>
                <CredentialingSection />
              </div>
            </Box>

            <EditableSectionTipWidth setTipWidth={setTipWidth} />

          </Box>
        </Container>
      </EditableSectionListContext.Provider>
    </EditableSectionsContext.Provider>
  );
};
